import React, { PropsWithChildren, useRef } from 'react';
import _isEmpty from 'lodash/isEmpty';
import Message from '@app/component/message/Message';
import Tag from '@app/component/tag/Tag';
import _capitalize from 'lodash/capitalize';
import Utilities from '@util/Utilities';
import ButtonLinkList from '@app/component/button-link-list/ButtonLinkList';
import AlertType from '@app/model/alert/AlertType';
import _lowerCase from 'lodash/lowerCase';
import useDialogue from '@app/component/overlays/dialog/DialogueRef';
import Dialogue from '@app/component/overlays/dialog/Dialogue';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import AlertLogDrawer from '@app/site/secured/alert/drawer/AlertLogDrawer';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertQueryKeys } from '@app/model/alert/service/AlertQueryKeys';
import AlertService from '@app/model/alert/service/AlertService';
import AlertOutput from '@app/model/alert/AlertOutput';
import { useSelectedCompanyHotel } from '@app/site/secured/provider/useSelectedCompanyHotel';

const AlertsPage = React.memo(() => {

	return <CustomAlerts />;

});

const CustomAlerts = React.memo(() => {

	const companyHotel = useSelectedCompanyHotel();

	if (!!companyHotel) {

		const { data: alertOutputs, isLoading, isSuccess } = useQuery({
			queryFn: async () => {
				return AlertService.findAlerts(companyHotel.hotel.id).then(data => {
					return Promise.resolve(!data ? [] : data);
				});
			},
			queryKey: AlertQueryKeys.list(companyHotel.hotel.id),
			initialData: () => [],
		});

		if (isSuccess) {
			return <Alerts companyHotel={companyHotel} alertOutputs={alertOutputs} />;
		}
	}
	return <></>;
});

const Alerts: React.FunctionComponent<{ companyHotel: CompanyHotel, alertOutputs: AlertOutput[] }> = ({
																																																				companyHotel,
																																																				alertOutputs,
																																																			}) => {

	const logDrawer = useRef<any>(null);
	const dialog = useDialogue<number>();
	const toaster = useToaster();
	const { t } = useTranslation();

	const queryClient = useQueryClient();
	const removeAlertMutation = useMutation({
		mutationFn: AlertService.removeAlert,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: AlertQueryKeys.lists() });
			toaster.success('Delete Alert', 'Alert deleted successfully!');
		},
		onError: (e: string) => toaster.error('Delete Alert', t(e)),
	});

	const removeAlert = () => {
		const alertId = dialog.getValue();
		removeAlertMutation.mutate({ hotelId: companyHotel.hotel.id, alertId: alertId! });
	};

	const findRoomTypeName = (roomType: HotelRoomMappingType) => {
		if (!!companyHotel.roomMappingTypeConfiguration) {
			const item = companyHotel.roomMappingTypeConfiguration.configs.find(c => c.type === roomType);
			if (!!item) return Utilities.snakeCaseToWords(item.name);
		}
		return '-';
	};

	return <>
		<Wrapper>

			<div
				className="mt-4 pb-3 flex flex-wrap items-center justify-between sm:flex-nowrap border-b border-gray-200 py-8">
				<div><h3 className="text-base font-semibold leading-6 text-gray-900">Get notified for market
					changes</h3><p
					className="text-sm text-gray-500">Customize the alerts below to trigger them as desired.</p>
				</div>
				<div className="ml-4 mt-4 flex-shrink-0">
					<ButtonLinkList items={[
						{ name: 'Rate Comparison', link: `/secured/create-alert/${AlertType.PRICE_COMPARISON}` },
						{
							name: 'Competitors Rate Change',
							link: `/secured/create-alert/${AlertType.COMPETITORS_PRICE_RATE}`,
						},
					]}>Add New Alert</ButtonLinkList>
				</div>
			</div>

			{_isEmpty(alertOutputs) && <Message state="info" extraClassName="mt-8 ring-1 ring-blue-500">
				No alerts found!
			</Message>}
			{!_isEmpty(alertOutputs) && <div
				className="mx-auto mt-8 max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none bg-white p-4 sm:p-10 rounded-xl">
				<div>

					<div className="flow-root">
						<div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
							<div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
								<table className="min-w-full divide-y divide-gray-300 text-gray-500">
									<thead>
									<tr>
										<th scope="col"
												className="whitespace-normal py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">Name
										</th>
										<th scope="col"
												className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">Created
										</th>
										<th scope="col"
												className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Type
										</th>
										<th scope="col" colSpan={2}
												className="whitespace-normal px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Ruleset
										</th>
										<th scope="col"
												className="whitespace-normal px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Room
											Types
										</th>
										<th scope="col"
												className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Rate
										</th>
										<th scope="col"
												className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Status
										</th>
										<th scope="col"
												className="relative py-3.5 pl-3 pr-4 sm:pr-0"></th>
									</tr>
									</thead>
									<tbody className="divide-y divide-gray-200 bg-white">
									{alertOutputs!.map(alertOutput => <tr key={alertOutput.alert.id}>
										<td className="whitespace-normal py-3 pl-4 pr-3 sm:pl-0 text-sm font-medium text-gray-900">
											{alertOutput.alert.name}
										</td>
										<td
											className="px-2 py-3 text-sm">{Utilities.formatDateString(alertOutput.alert.creationDate, 'DD/MM/YYYY HH:mm')}</td>
										<td
											className="px-2 py-3 text-sm">{alertOutput.alert.type === AlertType.PRICE_COMPARISON? 'Rate Comparison' : "Competitors Rate Change"}</td>
										<TableSegmentPerAlertType alertOutput={alertOutput} />
										<td className="whitespace-normal px-2 py-3 text-sm">
											{_isEmpty(alertOutput.alert.filter.roomTypes) ? 'All Room Types' : alertOutput.alert.filter.roomTypes.map(rt => findRoomTypeName(rt)).join(', ')}
										</td>

										<td className="whitespace-normal px-2 py-3 text-sm">
											{alertOutput.alert.filter.onlyRefundable ? 'Lowest Flex' : 'Lowest'}
										</td>
										<td className="px-2 py-3 text-sm">
											<Tag state="success">{_capitalize(alertOutput.alert.state)}</Tag>
										</td>
										<td className="px-2 py-3 text-sm">
											<ButtonLinkList portal items={[
												{
													name: 'Edit',
													link: `/secured/alert/${alertOutput.alert.type}/${alertOutput.alert.id}`,
												},
												{
													name: 'Duplicate',
													link: `/secured/duplicate-alert/${alertOutput.alert.type}/${alertOutput.alert.id}`,
												},
												{ name: 'Delete', onClick: () => dialog.open(alertOutput.alert.id) },
												{ name: 'Log', onClick: () => logDrawer.current!.open(alertOutput.alert) },
											]} />
										</td>
									</tr>)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>}
		</Wrapper>
		<Dialogue ref={dialog.ref} title="Delete Alert" onConfirm={removeAlert}>
			Are you sure you want to delete this alert?
		</Dialogue>
		<AlertLogDrawer ref={logDrawer} />
	</>;
};

const Wrapper: React.FunctionComponent<PropsWithChildren> = React.memo(({ children }) => {
	return <>
		<main>
			<div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
				{children}
			</div>
		</main>
	</>;
});

const TableSegmentPerAlertType: React.FunctionComponent<{ alertOutput: AlertOutput }> = ({ alertOutput }) => {

	const ruleDescription = (description: string, details: string) => {
		return <div className="flex flex-col">
			<span className="text-sm">{description}</span>
			<span className="italic text-xs text-gray-400">({details})</span>
		</div>;
	};

	return <>
		<td className="px-2 py-3 text-sm">
			<div className="flex flex-col">
				<span>{alertOutput.competitors[0].name}</span>
				{alertOutput.competitors.length > 1 && <span
					className="italic text-xs text-gray-400">(+ {alertOutput.competitors.length - 1} more Competitors)</span>}
			</div>
		</td>
		<td className="px-2 py-3 text-sm">
			{alertOutput.alert.type === AlertType.PRICE_COMPARISON && ruleDescription(`${alertOutput.priceComparisonAlertRule.priceRange!.from}% to ${alertOutput.priceComparisonAlertRule.priceRange!.to}%`, `for ${_lowerCase(alertOutput.priceComparisonAlertRule.trigger)}`)}
			{alertOutput.alert.type === AlertType.COMPETITORS_PRICE_RATE && ruleDescription(`${alertOutput.competitorsPriceRateAlertRule.priceRatePercentage}%`, `for ${_lowerCase(alertOutput.competitorsPriceRateAlertRule.calculation)}`)}
		</td>

	</>;
};

export default AlertsPage;